import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Star, ArrowLeft, Check } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

export default function FanClub() {
  const { creatorId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/fanclub/creator/${creatorId}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load this Fan Club.");
    } finally {
      setLoading(false);
    }
  }, [creatorId]);

  useEffect(() => { load(); }, [load]);

  // Return from PayPal approval → activate the membership.
  useEffect(() => {
    const subId = params.get("subscription_id");
    if (!subId) return;
    (async () => {
      try {
        const { data } = await api.post("/fanclub/confirm", { subscription_id: subId });
        toast[data.ok ? "success" : "message"](data.ok ? "You're now a Fan Club member! 💛" : "Membership is pending payment approval.");
      } catch {
        toast.error("Couldn't confirm the membership.");
      } finally {
        params.delete("subscription_id");
        setParams(params, { replace: true });
        load();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async (tierId) => {
    setBusy(tierId);
    try {
      const returnUrl = `${window.location.origin}/fanclub/${creatorId}`;
      const { data } = await api.post("/fanclub/subscribe", {
        creator_id: creatorId, tier_id: tierId,
        return_url: returnUrl, cancel_url: returnUrl,
      });
      if (data.approval_url) { window.location.href = data.approval_url; return; }
      toast.message("Subscription started.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the subscription.");
    } finally { setBusy(""); }
  };

  const cancel = async (membershipId) => {
    if (!window.confirm("Cancel this Fan Club membership? You keep access until the period ends.")) return;
    setBusy("cancel");
    try {
      await api.post("/fanclub/cancel", { membership_id: membershipId });
      toast.success("Membership cancelled.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't cancel.");
    } finally { setBusy(""); }
  };

  const toggleEnable = async (enabled) => {
    setBusy("enable");
    try {
      await api.post("/fanclub/enable", { enabled });
      toast.success(enabled ? "Fan Club opened 🎉" : "Fan Club closed.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update your Fan Club.");
    } finally { setBusy(""); }
  };

  if (loading) return <div className="min-h-[100dvh] bg-ink flex items-center justify-center text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading…</div>;
  if (!data) return null;

  const { creator, enabled, is_self, tiers = [], fan_count = 0, my_membership } = data;
  const activeMember = my_membership && my_membership.status === "active";

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="fanclub-page">
      <div className="px-5 pt-6 flex items-center gap-3">
        <button data-testid="fanclub-back" onClick={() => navigate(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <h1 className="text-cream text-lg font-black tracking-tight">Fan Club</h1>
      </div>

      <div className="px-5 mt-6 flex flex-col items-center text-center">
        <div className="w-24 h-24 rounded-full overflow-hidden crystal-glass flex items-center justify-center text-3xl text-cream mb-3">
          {creator.avatar_path ? <img src={fileUrl(creator.avatar_path)} alt="" className="w-full h-full object-cover" /> : (creator.display_name || creator.username || "?")[0]?.toUpperCase()}
        </div>
        <p className="text-cream text-xl font-black">{creator.display_name || creator.username}</p>
        <p className="text-cream/70 text-sm">@{creator.username}</p>
        <p className="mt-2 text-cream/75 text-xs tracking-[0.2em] uppercase" data-testid="fanclub-fancount">
          <Crown size={12} weight="fill" className="inline mb-0.5 text-papaya" /> {fan_count} {fan_count === 1 ? "fan" : "fans"}
        </p>
      </div>

      {/* Self view: open/close the Fan Club */}
      {is_self && (
        <div className="px-5 mt-8 max-w-md mx-auto" data-testid="fanclub-manage">
          <div className="crystal-glass rounded-3xl p-6">
            <p className="text-cream font-black text-base mb-1">Manage your Fan Club</p>
            <p className="text-cream/60 text-sm mb-5">Fans pay a monthly membership (real money via PayPal). You keep the creator share; perks include fan-only posts &amp; LIVE chat.</p>
            <button
              data-testid="fanclub-toggle-enable"
              disabled={busy === "enable"}
              onClick={() => toggleEnable(!enabled)}
              className={`w-full py-3 rounded-full text-xs font-black tracking-[0.2em] uppercase transition-colors ${enabled ? "bg-white/5 border border-white/10 text-cream" : "bg-papaya text-ink hover:bg-papaya-hover"}`}
            >
              {enabled ? "Close Fan Club" : "Open Fan Club"}
            </button>
          </div>
        </div>
      )}

      {/* Not enabled */}
      {!is_self && !enabled && (
        <div className="px-5 mt-10 text-center text-cream/70 text-sm" data-testid="fanclub-closed">
          This creator hasn&apos;t opened their Fan Club yet.
        </div>
      )}

      {/* Active membership banner */}
      {!is_self && activeMember && (
        <div className="px-5 mt-8 max-w-md mx-auto" data-testid="fanclub-active">
          <div className="crystal-glass rounded-3xl p-5 flex items-center justify-between">
            <div>
              <p className="text-papaya text-xs font-black tracking-[0.2em] uppercase">Active · {my_membership.tier_label}</p>
              <p className="text-cream/60 text-xs mt-1">${my_membership.price_usd}/mo</p>
            </div>
            <button data-testid="fanclub-cancel" disabled={busy === "cancel"} onClick={() => cancel(my_membership.id)} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-cream text-[11px] font-black tracking-widest uppercase">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tiers */}
      {!is_self && enabled && !activeMember && (
        <div className="px-5 mt-8 max-w-md mx-auto space-y-4" data-testid="fanclub-tiers">
          {tiers.map((t) => (
            <div key={t.id} className="crystal-glass rounded-3xl p-6" data-testid={`fanclub-tier-${t.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {t.id === "fan50" ? <Star size={20} weight="fill" className="text-papaya" /> : <Crown size={20} weight="fill" className="text-papaya" />}
                  <p className="text-cream font-black text-lg">{t.label}</p>
                </div>
                <p className="text-cream font-black text-xl">${t.price_usd}<span className="text-cream/50 text-xs font-bold">/mo</span></p>
              </div>
              <ul className="mt-4 space-y-2">
                {(t.perks || []).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-cream/75 text-sm">
                    <Check size={16} weight="bold" className="text-papaya mt-0.5 shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`fanclub-join-${t.id}`}
                disabled={busy === t.id}
                onClick={() => subscribe(t.id)}
                className="mt-5 w-full py-3 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.2em] uppercase hover:bg-papaya-hover transition-colors disabled:opacity-60"
              >
                {busy === t.id ? "Starting…" : `Join · $${t.price_usd}/mo`}
              </button>
            </div>
          ))}
          <p className="text-center text-cream/40 text-[11px] px-4">Billed monthly via PayPal. Cancel anytime. Fan Club is separate from WAVE — this is a real-money subscription.</p>
        </div>
      )}
    </div>
  );
}
